/* eslint-disable react/prop-types */
/* eslint-disable react/display-name */
import React from 'react';
import styled from 'styled-components';
import { variables } from './variables';

interface FlexBoxProps {
  justify?: 'center' | 'space-between' | 'space-around' | 'flex-end' | 'flex-start';
  direction?: 'row' | 'row-reverse' | 'column' | 'column-reverse';
  alignItems?: 'stretch' | 'flex-start' | 'flex-end' | 'center' | 'baseline';
  alignContent?: 'space-between' | 'flex-start' | 'flex-end' | 'center' | 'space-around' | 'space-evenly' | 'stretch';
  wrap?: 'nowrap' | 'wrap' | 'wrap-reverse';
  fullheight?: boolean;
  fullwidth?: boolean;
  className?: string;
  children: React.ReactNode;
}
export const FlexBox = styled(
  ({
    justify,
    direction,
    alignItems,
    alignContent,
    wrap,
    children,
    fullwidth,
    fullheight,
    className,
    ...props
  }: FlexBoxProps) => (
    <div className={className} {...props}>
      {children}
    </div>
  ),
)`
  display: flex;
  ${({ justify }) =>
    justify &&
    `
    justify-content: ${justify};
  `}
  ${({ direction }) =>
    direction &&
    `
    flex-direction: ${direction};
  `}
  ${({ alignItems }) =>
    alignItems &&
    `
    align-items: ${alignItems};
  `}
  ${({ alignContent }) =>
    alignContent &&
    `
    align-content: ${alignContent};
  `}
  ${({ fullheight }) =>
    fullheight &&
    `
    height: 100%};
  `}
  ${({ fullwidth }) =>
    fullwidth &&
    `
    width: 100%;
  `}
  ${({ wrap }) =>
    wrap &&
    `
  flex-wrap: ${wrap};
`}
`;

export const DynamicTabContainer = styled(FlexBox)`
  height: 100%;
  @media (max-width: 1500px) and (min-width: 769px), (max-width: 1024px) {
    flex-direction: column;
    width: 100%;
  }
  h2 {
    margin-top: 20px;
  }
  .formElementContainer {
    .formElementHeader {
      font-weight: bold;
      margin-top: 20px;
      margin-bottom: 5px;
    }
  }
`;
export const AdditionalButtonsContainer = styled.div`
  display: flex;
  margin-left: 3rem;
  .ant-btn {
    padding: 8px 20px;
    margin-top: 0;
    &:not(:first-child) {
      margin-left: 10px;
    }
  }
  @media (max-width: 768px) {
    margin-left: 2rem;
    .ant-btn {
      font-size: 1rem;
    }
    @media (max-width: 568px), (max-height: 412px) {
      margin-left: 0;
      margin-bottom: 0.4rem;
    }
  }
`;
export interface StaticButtonsContainerProps {
  paddingBottom: number;
  paddingTop: number;
  children: JSX.Element;
  [key: string]: unknown;
}
export const StaticButtonsContainer = styled(({ paddingBottom, paddingTop, children, ...props }: StaticButtonsContainerProps) => (
  <FlexBox wrap='wrap' justify='flex-end' {...props}>
    {children}
  </FlexBox>
))`
  padding-top: ${props => (props.paddingTop ? `${props.paddingTop}px` : 0)};
  padding-bottom: ${props => (props.paddingBottom ? `${props.paddingBottom}px` : 0)};
  button {
    margin: 0 0 8px 8px;
  }
`;

export interface TabRightPanelProps {
  withFullWidth: boolean;
  [key: string]: unknown;
}
export const TabRightPanel = styled(({ withFullWidth, ...props }: TabRightPanelProps) => <div {...props} />)`
  height: 100%;
  position: relative;
  width: ${({ withFullWidth }) => (withFullWidth ? '99%' : 'calc(100% - 210px)')};
`;

export interface FormContainerProps {
  overflow: boolean;
  [key: string]: unknown;
}

export const FormContainer = styled(({ overflow, ...props }: FormContainerProps) => <div {...props} />)`
  ${({ overflow }) =>
    overflow &&
    `
    overflow: auto;
    height: calc(100% - 120px);
    padding: 20px 10px 10px 10px;
  `};

  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
    border-color: ${variables.colorWhite};
    background: ${variables.colorWhite};
    -webkit-appearance: none;
  }
  &::-webkit-scrollbar-thumb {
    background: ${variables.colorGrey};
    border-radius: 8px;

    &:hover {
      background: ${variables.colorGreyDarkest};
      border-radius: 8px;
    }

    &:active {
      background: ${variables.colorDecorator};
      border-radius: 8px;
    }
  }

  .custom_select {
    min-width: 200px;
    max-width: 100%;
    width: 350px;
  }
  .ant-select-selector {
    height: unset !important;
  }
  span.centered {
    font-size: 25px;
    padding: 10px;
  }
  @media (max-height: 667px) {
    height: 100%;
  }
`;
interface TableContainerProps {
  loading: boolean;
  [key: string]: any;
}
export const AboveTableContainer = styled.div`
  padding: 1rem 3rem 1rem 3rem;
  button {
    margin-top: 0;
    margin-bottom: 10px;
    margin-right: 10px;
    &,
    & span {
      font-weight: 200;
    }
  }
  button:not([aria-label='menu']) {
    height: max-content;
  }
  @media (max-width: 1500px) and (min-width: 769px) {
    padding: 1rem 3rem 1rem 0;
  }
  @media (max-width: 768px) {
    padding: 0 0rem 1rem 0px;
  }
`;

interface FilterContainerProps {
  children: JSX.Element;
  [key: string]: unknown;
}

interface HeaderWrapperProps {
  children: React.ReactNode;
  smaller: boolean;
}

export const FilterContainer = styled(({ children, ...props }: FilterContainerProps) => (
  <FlexBox justify='space-between' {...props}>
    {children}
  </FlexBox>
))`
  position: relative;
  @media (max-width: 576px) {
    flex-direction: column;
    padding: 0;
  }
`;

export const TableContainer = styled(({ loading, children, ...props }: TableContainerProps) => (
  <FlexBox direction='row-reverse' alignItems='flex-start' {...props}>
    {children}
  </FlexBox>
))`
  opacity: ${({ loading }: TableContainerProps): string => (loading ? '0.2' : '1')};
  cursor: ${({ loading }: TableContainerProps): string => (loading ? 'not-allowed' : 'auto')};
  pointer-events: ${({ loading }: TableContainerProps): string => (loading ? 'none' : 'auto')};
  width: 100%;
  position: relative;
  & > .ant-table-wrapper {
    width: calc(100% - 70px);
  }
  @media (max-width: 1500px) {
    & > .ant-table-wrapper {
      width: calc(100% - 40px);
    }
  }
`;

export const CustomScrollbarContainer = styled.div`
  overflow: auto;
  height: 100%;
  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
    border-color: ${variables.colorWhite};
    background: ${variables.colorWhite};
    -webkit-appearance: none;
  }
  &::-webkit-scrollbar-thumb {
    background: ${variables.colorGrey};
    border-radius: 8px;

    &:hover {
      background: ${variables.colorGreyDarkest};
      border-radius: 8px;
    }

    &:active {
      background: ${variables.colorDecorator};
      border-radius: 8px;
    }
  }
`;

interface PrintableBodyWrapperProps {
  children: React.ReactNode;
}

export const PrintableBodyWrapper = styled.div<PrintableBodyWrapperProps>`
  display: none;
  @media print {
    display: block;
    background-color: white;
    width: 100vw;
  }
`;

export const HeaderWrapper = styled(({ smaller, children, ...props }: HeaderWrapperProps) => <div {...props}>{children}</div>)`
  font-weight: bold;
  font-size: ${({ smaller }: HeaderWrapperProps): string => (smaller ? '3.6rem' : '4.2rem')};
  padding: 2rem 0;

  span {
    font-size: ${({ smaller }: HeaderWrapperProps): string => (smaller ? '2.4rem' : '3.2rem')};
    display: block;
  }
`;
